import { useMemo } from 'react';

/**
 * Hook to generate search suggestions based on recipes and inventory
 * 
 * @param {Array} recipes - Array of recipe objects
 * @param {Array} inventory - Array of inventory ingredient objects
 * @param {string} searchInput - Current search input
 * @returns {Array} - Filtered search suggestions
 */
const useSearchSuggestions = (recipes, inventory, searchInput) => {
  const suggestions = useMemo(() => {
    // Build suggestions from inventory and recipes.
    let result = [];
    
    // Add fallback 'search for: <query>' option at the top if search input is not empty
    if (searchInput.trim() !== '') {
      result.push({ 
        type: 'search', 
        id: 'search', 
        name: `Search for: ${searchInput}`,
        group: '' // Empty group for search option
      });
    }

    // Cocktails (recipes)
    const cocktailSuggestions = recipes.map(recipe => ({
      type: 'recipe',
      id: recipe.id,
      name: `${recipe.name} (${recipe.origin})`,
      rawName: recipe.name,
      origin: recipe.origin,
      group: 'Cocktails' // Add group property instead of separate entry
    })).filter(recipe => 
      recipe.rawName.toLowerCase().includes(searchInput.toLowerCase())
    );

    // Add cocktail suggestions (limited to 5)
    if (cocktailSuggestions.length > 0) {
      result = result.concat(cocktailSuggestions.slice(0, 5));
    }

    // Ingredients
    const ingredientSuggestions = inventory.map(ing => ({
      type: 'ingredient',
      id: ing.id,
      name: ing.name,
      group: 'Ingredients' // Add group property instead of separate entry
    })).filter(item => 
      item.name.toLowerCase().includes(searchInput.toLowerCase())
    );

    // Add ingredient suggestions (limited to 5)
    if (ingredientSuggestions.length > 0) {
      result = result.concat(ingredientSuggestions.slice(0, 5));
    }

    // Add 'no matches' option if needed
    if (result.length === 0 && searchInput.trim() !== '') {
      result = [{ 
        type: 'no-matches', 
        id: 'no-matches', 
        name: 'No matches found', 
        disabled: true,
        group: ''
      }];
    }

    return result;
  }, [recipes, inventory, searchInput]);

  return suggestions;
};

export default useSearchSuggestions; 